import React, { useState } from 'react';
import { alpha } from '@mui/material/styles';
import {
  Box,
  Paper,
  Typography,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Stack,
  Chip,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton
} from '@mui/material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import {
  Close as CloseIcon,
  ZoomOutMap as ExpandIcon
} from '@mui/icons-material';
import { ccyFormat } from '../../helpers/helper';

const SalesCharts = ({ stats }) => {
  const [chartView, setChartView] = useState('sales'); // 'sales' o 'revenue'
  const [sellerView, setSellerView] = useState('all'); // 'all' o user_id específico
  const [expandedChart, setExpandedChart] = useState(null); // 'daily', 'status', 'sellers', 'performance'
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const COLORS = [
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
    theme.palette.info.main,
    theme.palette.secondary.main,
    theme.palette.primary.main,
    theme.palette.warning.light,
    theme.palette.error.light
  ];
  const bestDayColor = theme.palette.warning.main;

  if (!stats) return null;

  // Preparar datos para el gráfico de ventas diarias
  const dailySalesData = stats.daily_sales_data || [];

  // Preparar datos para el gráfico de estados (Pie Chart)
  const statusData = [
    { name: 'Finalizadas', value: stats.sales_finalized || 0, color: theme.palette.success.main },
    { name: 'Reservadas', value: stats.sales_reserved || 0, color: theme.palette.warning.main },
    { name: 'Canceladas', value: stats.sales_canceled || 0, color: theme.palette.error.main },
    { name: 'Devueltas', value: stats.sales_returned || 0, color: theme.palette.info.main }
  ].filter(item => item.value > 0);

  // Preparar datos para gráfico de vendedores
  const sellerData = (stats.top_sellers || []).map(seller => ({
    name: seller.user_name,
    ventas: seller.sales,
    ingresos: seller.revenue,
    ticketPromedio: seller.avg_ticket
  }));

  // Preparar datos para gráfico de vendedor específico
  const getSellerDailyData = () => {
    if (sellerView === 'all') return null;
    const seller = (stats.seller_daily_sales || []).find(s => s.user_id === sellerView);
    return seller?.daily_sales || [];
  };

  // Preparar datos combinados para vista "Todos" los vendedores
  const getCombinedSellerData = () => {
    if (!stats.seller_daily_sales || stats.seller_daily_sales.length === 0) return [];
    
    // Crear un mapa de fechas con todos los vendedores
    const dateMap = {};
    
    // Iterar por cada vendedor y sus ventas diarias
    stats.seller_daily_sales.forEach(seller => {
      seller.daily_sales.forEach(daySale => {
        if (!dateMap[daySale.date]) {
          dateMap[daySale.date] = { date: daySale.date };
        }
        // Agregar las ventas de este vendedor a esta fecha
        dateMap[daySale.date][`${seller.user_id}_sales`] = daySale.total_sales;
        dateMap[daySale.date][`${seller.user_id}_revenue`] = daySale.revenue;
        dateMap[daySale.date][`${seller.user_name}`] = daySale.total_sales; // Para la línea
      });
    });
    
    // Convertir el mapa a array y ordenar por fecha
    return Object.values(dateMap).sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const combinedSellerData = getCombinedSellerData();
  const sellerDailyData = getSellerDailyData();

  // Encontrar el día con más ventas o ingresos
  const getBestDay = () => {
    if (!dailySalesData || dailySalesData.length === 0) return null;
    
    const metric = chartView === 'sales' ? 'total_sales' : 'revenue';
    const bestDay = dailySalesData.reduce((max, day) => {
      return (day[metric] > max[metric]) ? day : max;
    }, dailySalesData[0]);
    
    return bestDay?.date;
  };

  const bestDay = getBestDay();

  // Componente de dot personalizado para resaltar el mejor día
  const CustomDot = (props) => {
    const { cx, cy, payload } = props;
    const isBestDay = payload.date === bestDay;
    
    if (isBestDay) {
      return (
        <g>
          {/* Círculo exterior brillante */}
          <circle cx={cx} cy={cy} r={8} fill={bestDayColor} opacity={0.3} />
          {/* Círculo medio */}
          <circle cx={cx} cy={cy} r={5} fill={bestDayColor} opacity={0.6} />
          {/* Círculo interior */}
          <circle cx={cx} cy={cy} r={3} fill={bestDayColor} stroke={theme.palette.common.white} strokeWidth={1.5} />
        </g>
      );
    }
    
    return <circle cx={cx} cy={cy} r={3} fill={chartView === 'sales' ? theme.palette.primary.main : theme.palette.success.main} />;
  };

  // Tooltip personalizado para montos
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper sx={{ p: 2, border: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="body2" fontWeight={600} gutterBottom>
            {label}
          </Typography>
          {payload.map((entry, index) => (
            <Typography key={index} variant="body2" color={entry.color}>
              {entry.name}: {entry.name.toLowerCase().includes('ingreso') || entry.name.toLowerCase().includes('revenue') 
                ? `$${ccyFormat(entry.value)}` 
                : entry.value}
            </Typography>
          ))}
        </Paper>
      );
    }
    return null;
  };

  return (
    <Box sx={{
      // Eliminar el highlight negro en móviles al tocar
      WebkitTapHighlightColor: 'transparent',
      '& .recharts-wrapper': {
        WebkitTapHighlightColor: 'transparent',
      }
    }}>
      {/* Gráfico de Ventas Diarias */}
      <Paper elevation={0} sx={{ 
        p: isMobile ? 2 : 3, 
        mb: 4, 
        borderRadius: 3, 
        border: `1px solid ${theme.palette.divider}`,
        WebkitTapHighlightColor: 'transparent',
        position: 'relative'
      }}>
        {isMobile && (
          <IconButton
            onClick={() => setExpandedChart('daily')}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 1,
              bgcolor: alpha(theme.palette.background.paper, 0.9),
              boxShadow: 1,
              WebkitTapHighlightColor: 'transparent',
              '&:hover': {
                bgcolor: theme.palette.background.paper,
              }
            }}
            size="small"
          >
            <ExpandIcon fontSize="small" />
          </IconButton>
        )}
        <Stack 
          direction={isMobile ? "column" : "row"} 
          justifyContent="space-between" 
          alignItems={isMobile ? "flex-start" : "center"} 
          spacing={isMobile ? 2 : 0}
          mb={2}
        >
          <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight={600}>
            Evolución Diaria
          </Typography>
          <ToggleButtonGroup
            value={chartView}
            exclusive
            onChange={(e, newView) => newView && setChartView(newView)}
            size="small"
            sx={{
              WebkitTapHighlightColor: 'transparent',
              '& .MuiToggleButton-root': {
                WebkitTapHighlightColor: 'transparent',
                touchAction: 'manipulation'
              }
            }}
          >
            <ToggleButton value="sales">
              Cantidad
            </ToggleButton>
            <ToggleButton value="revenue">
              Ingresos
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        <Divider sx={{ mb: 2 }} />
        
        {/* Indicador del mejor día */}
        {bestDay && (
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: bestDayColor,
                boxShadow: `0 0 8px ${alpha(bestDayColor, 0.5)}`
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}>
              Día con más {chartView === 'sales' ? 'ventas' : 'ingresos'}: {' '}
              <strong style={{ color: bestDayColor }}>
                {(() => {
                  const [year, month, day] = bestDay.split('-').map(Number);
                  const d = new Date(year, month - 1, day);
                  return d.toLocaleDateString('es-AR', { 
                    day: '2-digit', 
                    month: isMobile ? 'short' : 'long',
                    year: isMobile ? undefined : 'numeric'
                  });
                })()}
              </strong>
            </Typography>
          </Stack>
        )}
        
        <ResponsiveContainer width="100%" height={isMobile ? 250 : 350}>
          <LineChart data={dailySalesData}>
            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
            <XAxis 
              dataKey="date" 
              tick={{ fontSize: 12 }}
              tickFormatter={(date) => {
                const [year, month, day] = date.split('-').map(Number);
                const d = new Date(year, month - 1, day);
                return `${d.getDate()}/${d.getMonth() + 1}`;
              }}
            />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            {chartView === 'sales' ? (
              <Line 
                type="monotone" 
                dataKey="total_sales" 
                name="Ventas" 
                stroke={theme.palette.primary.main} 
                strokeWidth={2}
                dot={<CustomDot />}
                activeDot={{ r: 5 }}
              />
            ) : (
              <Line 
                type="monotone" 
                dataKey="revenue" 
                name="Ingresos" 
                stroke={theme.palette.success.main} 
                strokeWidth={2}
                dot={<CustomDot />}
                activeDot={{ r: 5 }}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </Paper>

      <Grid container spacing={isMobile ? 2 : 3} mb={4}>
        {/* Gráfico de Distribución por Estado */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ 
            p: isMobile ? 2 : 3, 
            borderRadius: 3, 
            border: `1px solid ${theme.palette.divider}`, 
            height: '100%',
            WebkitTapHighlightColor: 'transparent',
            position: 'relative'
          }}>
            {isMobile && (
              <IconButton
                onClick={() => setExpandedChart('status')}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 1,
                  bgcolor: alpha(theme.palette.background.paper, 0.9),
                  boxShadow: 1,
                  WebkitTapHighlightColor: 'transparent',
                  '&:hover': {
                    bgcolor: theme.palette.background.paper,
                  }
                }}
                size="small"
              >
                <ExpandIcon fontSize="small" />
              </IconButton>
            )}
            <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight={600} gutterBottom>
              Distribución por Estado
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={isMobile ? false : ({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={isMobile ? 80 : 100}
                  fill={theme.palette.secondary.main}
                  dataKey="value"
                >
                  {statusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Gráfico de Top Vendedores */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ 
            p: isMobile ? 2 : 3, 
            borderRadius: 3, 
            border: `1px solid ${theme.palette.divider}`, 
            height: '100%',
            WebkitTapHighlightColor: 'transparent',
            position: 'relative'
          }}>
            {isMobile && (
              <IconButton
                onClick={() => setExpandedChart('sellers')}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 1,
                  bgcolor: alpha(theme.palette.background.paper, 0.9),
                  boxShadow: 1,
                  WebkitTapHighlightColor: 'transparent',
                  '&:hover': {
                    bgcolor: theme.palette.background.paper,
                  }
                }}
                size="small"
              >
                <ExpandIcon fontSize="small" />
              </IconButton>
            )}
            <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight={600} gutterBottom>
              Top Vendedores
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
              <BarChart data={sellerData}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                <XAxis 
                  dataKey="name" 
                  tick={{ fontSize: 11 }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="ventas" name="Ventas" fill={theme.palette.primary.main} radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Gráfico de Ventas por Vendedor (Detalle) */}
      {stats.seller_daily_sales && stats.seller_daily_sales.length > 0 && (
        <Paper elevation={0} sx={{ 
          p: isMobile ? 2 : 3, 
          borderRadius: 3, 
          border: `1px solid ${theme.palette.divider}`,
          WebkitTapHighlightColor: 'transparent',
          position: 'relative'
        }}>
          {isMobile && (
            <IconButton
              onClick={() => setExpandedChart('performance')}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 1,
                bgcolor: alpha(theme.palette.background.paper, 0.9),
                boxShadow: 1,
                WebkitTapHighlightColor: 'transparent',
                '&:hover': {
                  bgcolor: theme.palette.background.paper,
                }
              }}
              size="small"
            >
              <ExpandIcon fontSize="small" />
            </IconButton>
          )}
          <Stack 
            direction={isMobile ? "column" : "row"} 
            justifyContent="space-between" 
            alignItems={isMobile ? "flex-start" : "center"} 
            mb={2} 
            flexWrap="wrap" 
            gap={2}
          >
            <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight={600}>
              Desempeño por Vendedor
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{
              WebkitTapHighlightColor: 'transparent',
              gap: 1
            }}>
              <Chip
                label="Todos"
                onClick={() => setSellerView('all')}
                color={sellerView === 'all' ? 'primary' : 'default'}
                variant={sellerView === 'all' ? 'filled' : 'outlined'}
                size={isMobile ? "small" : "medium"}
                sx={{ 
                  WebkitTapHighlightColor: 'transparent',
                  touchAction: 'manipulation'
                }}
              />
              {stats.seller_daily_sales.map((seller, index) => (
                <Chip
                  key={seller.user_id}
                  label={seller.user_name}
                  onClick={() => setSellerView(seller.user_id)}
                  color={sellerView === seller.user_id ? 'primary' : 'default'}
                  variant={sellerView === seller.user_id ? 'filled' : 'outlined'}
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    bgcolor: sellerView === seller.user_id ? COLORS[index % COLORS.length] : undefined,
                    WebkitTapHighlightColor: 'transparent',
                    touchAction: 'manipulation',
                    '&:hover': {
                      bgcolor: sellerView === seller.user_id ? COLORS[index % COLORS.length] : undefined,
                    }
                  }}
                />
              ))}
            </Stack>
          </Stack>
          <Divider sx={{ mb: 3 }} />
          
          <ResponsiveContainer width="100%" height={isMobile ? 250 : 350}>
            <LineChart data={sellerView === 'all' ? combinedSellerData : sellerDailyData}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: 12 }}
                tickFormatter={(date) => {
                  const [year, month, day] = date.split('-').map(Number);
                  const d = new Date(year, month - 1, day);
                  return `${d.getDate()}/${d.getMonth() + 1}`;
                }}
              />
              <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              
              {sellerView === 'all' ? (
                <>
                  {stats.seller_daily_sales.map((seller, index) => (
                    <Line
                      key={seller.user_id}
                      type="monotone"
                      dataKey={seller.user_name}
                      name={seller.user_name}
                      stroke={COLORS[index % COLORS.length]}
                      strokeWidth={2}
                      dot={{ r: 2.5 }}
                      yAxisId="left"
                      connectNulls
                    />
                  ))}
                </>
              ) : (
                <>
                  <Line 
                    type="monotone" 
                    dataKey="total_sales" 
                    name="Ventas" 
                    stroke={theme.palette.primary.main} 
                    strokeWidth={2}
                    dot={{ fill: theme.palette.primary.main, r: 3 }}
                    yAxisId="left"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="revenue" 
                    name="Ingresos" 
                    stroke={theme.palette.success.main} 
                    strokeWidth={2}
                    dot={{ fill: theme.palette.success.main, r: 3 }}
                    yAxisId="right"
                  />
                </>
              )}
            </LineChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {/* Modales para expandir gráficos en móvil */}
      <Dialog
        open={expandedChart === 'daily'}
        onClose={() => setExpandedChart(null)}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            WebkitTapHighlightColor: 'transparent',
            bgcolor: 'background.default'
          }
        }}
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={600}>
              Evolución Diaria
            </Typography>
            <IconButton 
              onClick={() => setExpandedChart(null)}
              sx={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <ToggleButtonGroup
            value={chartView}
            exclusive
            onChange={(e, newView) => newView && setChartView(newView)}
            size="small"
            sx={{
              mb: 2,
              WebkitTapHighlightColor: 'transparent',
              '& .MuiToggleButton-root': {
                WebkitTapHighlightColor: 'transparent',
              }
            }}
          >
            <ToggleButton value="sales">Cantidad</ToggleButton>
            <ToggleButton value="revenue">Ingresos</ToggleButton>
          </ToggleButtonGroup>
          
          {bestDay && (
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  bgcolor: bestDayColor,
                  boxShadow: `0 0 8px ${alpha(bestDayColor, 0.5)}`
                }}
              />
              <Typography variant="caption" color="text.secondary">
                Día con más {chartView === 'sales' ? 'ventas' : 'ingresos'}: {' '}
                <strong style={{ color: bestDayColor }}>
                  {(() => {
                    const [year, month, day] = bestDay.split('-').map(Number);
                    const d = new Date(year, month - 1, day);
                    return d.toLocaleDateString('es-AR', { 
                      day: '2-digit', 
                      month: 'long',
                      year: 'numeric'
                    });
                  })()}
                </strong>
              </Typography>
            </Stack>
          )}
          
          <Box sx={{ overflowX: 'auto', overflowY: 'hidden', pb: 2 }}>
            <Box sx={{ minWidth: Math.max(800, dailySalesData.length * 50) }}>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={dailySalesData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(date) => {
                      const [year, month, day] = date.split('-').map(Number);
                      const d = new Date(year, month - 1, day);
                      return `${d.getDate()}/${d.getMonth() + 1}`;
                    }}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  {chartView === 'sales' ? (
                    <Line 
                      type="monotone" 
                      dataKey="total_sales" 
                      name="Ventas" 
                      stroke={theme.palette.primary.main} 
                      strokeWidth={2}
                      dot={<CustomDot />}
                      activeDot={{ r: 5 }}
                    />
                  ) : (
                    <Line 
                      type="monotone" 
                      dataKey="revenue" 
                      name="Ingresos" 
                      stroke={theme.palette.success.main} 
                      strokeWidth={2}
                      dot={<CustomDot />}
                      activeDot={{ r: 5 }}
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        open={expandedChart === 'status'}
        onClose={() => setExpandedChart(null)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            WebkitTapHighlightColor: 'transparent',
            bgcolor: 'background.default'
          }
        }}
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={600}>
              Distribución por Estado
            </Typography>
            <IconButton 
              onClick={() => setExpandedChart(null)}
              sx={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <ResponsiveContainer width="100%" height={400}>
            <PieChart>
              <Pie
                data={statusData}
                cx="50%"
                cy="50%"
                labelLine={true}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={120}
                fill={theme.palette.secondary.main}
                dataKey="value"
              >
                {statusData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </DialogContent>
      </Dialog>

      <Dialog
        open={expandedChart === 'sellers'}
        onClose={() => setExpandedChart(null)}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            WebkitTapHighlightColor: 'transparent',
            bgcolor: 'background.default'
          }
        }}
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={600}>
              Top Vendedores
            </Typography>
            <IconButton 
              onClick={() => setExpandedChart(null)}
              sx={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={sellerData}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis 
                dataKey="name" 
                tick={{ fontSize: 12 }}
              />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar dataKey="ventas" name="Ventas" fill={theme.palette.primary.main} radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </DialogContent>
      </Dialog>

      <Dialog
        open={expandedChart === 'performance'}
        onClose={() => setExpandedChart(null)}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            WebkitTapHighlightColor: 'transparent',
            bgcolor: 'background.default'
          }
        }}
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={600}>
              Desempeño por Vendedor
            </Typography>
            <IconButton 
              onClick={() => setExpandedChart(null)}
              sx={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={1} flexWrap="wrap" mb={2} sx={{ gap: 1 }}>
            <Chip
              label="Todos"
              onClick={() => setSellerView('all')}
              color={sellerView === 'all' ? 'primary' : 'default'}
              variant={sellerView === 'all' ? 'filled' : 'outlined'}
              sx={{ 
                WebkitTapHighlightColor: 'transparent',
                touchAction: 'manipulation'
              }}
            />
            {stats.seller_daily_sales.map((seller, index) => (
              <Chip
                key={seller.user_id}
                label={seller.user_name}
                onClick={() => setSellerView(seller.user_id)}
                color={sellerView === seller.user_id ? 'primary' : 'default'}
                variant={sellerView === seller.user_id ? 'filled' : 'outlined'}
                sx={{
                  bgcolor: sellerView === seller.user_id ? COLORS[index % COLORS.length] : undefined,
                  WebkitTapHighlightColor: 'transparent',
                  touchAction: 'manipulation',
                  '&:hover': {
                    bgcolor: sellerView === seller.user_id ? COLORS[index % COLORS.length] : undefined,
                  }
                }}
              />
            ))}
          </Stack>
          
          <Box sx={{ overflowX: 'auto', overflowY: 'hidden', pb: 2 }}>
            <Box sx={{ minWidth: Math.max(800, (sellerView === 'all' ? combinedSellerData : sellerDailyData).length * 50) }}>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={sellerView === 'all' ? combinedSellerData : sellerDailyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(date) => {
                      const [year, month, day] = date.split('-').map(Number);
                      const d = new Date(year, month - 1, day);
                      return `${d.getDate()}/${d.getMonth() + 1}`;
                    }}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  {sellerView === 'all' ? (
                    stats.seller_daily_sales.map((seller, index) => (
                      <Line
                        key={seller.user_id}
                        type="monotone"
                        dataKey={seller.user_name}
                        name={seller.user_name}
                        stroke={COLORS[index % COLORS.length]}
                        strokeWidth={2}
                        connectNulls
                        dot={{ r: 2.5 }}
                      />
                    ))
                  ) : (
                    <>
                      <Line 
                        type="monotone" 
                        dataKey="total_sales" 
                        name="Ventas" 
                        stroke={theme.palette.primary.main} 
                        strokeWidth={2}
                        dot={{ fill: theme.palette.primary.main, r: 3 }}
                      />
                    </>
                  )}
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default SalesCharts;
